import * as React from 'react';
import {
  FileText,
  CheckCircle2,
  Database,
  Plus,
  Trash2,
  FileJson,
  Type,
  Clock,
  Hash,
  BookOpen
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SliderWithInput } from "@/components/ui/slider-with-input";
import { Combobox } from "@/components/ui/combobox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Assistant, AssistantAnalysisPlan } from '@/api/assistants/assistants.types';

interface AnalysisTabProps {
  assistant: Omit<Assistant, 'name'>;
  onUpdate: (updates: Partial<Assistant>) => void;
}

interface SchemaProperty {
  type: string;
  description?: string;
}

interface SchemaDefinition {
  type: string;
  properties: Record<string, SchemaProperty>;
  required?: string[];
}

const dataTypes = [
  'string',
  'number',
  'boolean',
  'array',
  'object'
] as const;

type DataType = typeof dataTypes[number];

interface Property {
  name: string;
  type: DataType;
  required: boolean;
  description?: string;
}

export function AnalysisTab({ assistant, onUpdate }: AnalysisTabProps) {
  const handleUpdate = (path: string[], value: unknown) => {
    const updates = { ...assistant };
    let current: Record<string, unknown> = updates;

    // Handle all but last item in path
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
      current[key] = { ...(current[key] as Record<string, unknown>) };
      current = current[key] as Record<string, unknown>;
    }

    // Set final value
    const lastKey = path[path.length - 1];
    if (lastKey === 'structuredDataSchema' && typeof value === 'object') {
      // Ensure schema is handled as an object
      current[lastKey] = value;
    } else {
      current[lastKey] = value;
    }

    onUpdate(updates as Assistant);
  };

  const handleSummaryUpdate = (content: string) => {
    const newAnalysisPlan: AssistantAnalysisPlan = {
      ...assistant.analysisPlan,
      summaryPrompt: content,
      summaryRequestTimeoutSeconds: assistant?.analysisPlan?.summaryRequestTimeoutSeconds || 20
    };

    onUpdate({
      ...assistant,
      analysisPlan: newAnalysisPlan
    });
  };

  const handleStructuredDataUpdate = (content: string) => {
    const newAnalysisPlan: AssistantAnalysisPlan = {
      ...assistant.analysisPlan,
      structuredDataPrompt: content,
      structuredDataSchema: assistant?.analysisPlan?.structuredDataSchema || {
        type: 'object',
        properties: {},
        required: []
      },
      structuredDataRequestTimeoutSeconds: assistant?.analysisPlan?.structuredDataRequestTimeoutSeconds || 10
    };

    onUpdate({
      ...assistant,
      analysisPlan: newAnalysisPlan
    });
  };

  const handleEvaluationUpdate = (content: string) => {
    const newAnalysisPlan: AssistantAnalysisPlan = {
      ...assistant.analysisPlan,
      successEvaluationPrompt: content,
      successEvaluationRubric: assistant?.analysisPlan?.successEvaluationRubric || 'NumericScale',
      successEvaluationRequestTimeoutSeconds: assistant?.analysisPlan?.successEvaluationRequestTimeoutSeconds || 20
    };

    onUpdate({
      ...assistant,
      analysisPlan: newAnalysisPlan
    });
  };

  // Parse existing schema to properties
  const [properties, setProperties] = React.useState<Property[]>(() => {
    try {
      const schema = assistant?.analysisPlan?.structuredDataSchema;
      if (!schema) return [];

      // If schema is a string (from API), parse it
      const schemaObj = typeof schema === 'string' ? JSON.parse(schema) : schema;

      return Object.entries(schemaObj.properties || {}).map(([name, prop]) => ({
        name,
        type: (prop as SchemaProperty).type as DataType,
        required: (schemaObj.required || []).includes(name),
        description: (prop as SchemaProperty).description
      }));
    } catch {
      return [];
    }
  });

  // Handle schema initialization when component mounts
  React.useEffect(() => {
    const schema = assistant?.analysisPlan?.structuredDataSchema;
    if (schema && typeof schema === 'string') {
      try {
        const parsedSchema = JSON.parse(schema);
        handleUpdate(['analysisPlan', 'structuredDataSchema'], parsedSchema);
      } catch {
        // If parsing fails, keep the string value
      }
    }
  }, []);

  // Convert properties to schema object
  const updateSchema = (newProperties: Property[]) => {
    const schema: SchemaDefinition = {
      type: 'object',
      properties: newProperties.reduce((acc, prop) => ({
        ...acc,
        [prop.name]: {
          type: prop.type,
          ...(prop.description ? { description: prop.description } : {})
        }
      }), {}),
      required: newProperties.filter(p => p.required).map(p => p.name)
    };

    // Pass the schema object directly
    handleUpdate(['analysisPlan', 'structuredDataSchema'], schema);
  };

  const addProperty = () => {
    const newProperty: Property = {
      name: '',
      type: 'string',
      required: false
    };
    const newProperties = [...properties, newProperty];
    setProperties(newProperties);
    updateSchema(newProperties);
  };

  const updateProperty = (index: number, key: keyof Property, value: Property[keyof Property]) => {
    const newProperties = properties.map((prop, i) =>
      i === index ? { ...prop, [key]: value } : prop
    );
    setProperties(newProperties);
    updateSchema(newProperties);
  };

  const removeProperty = (index: number) => {
    const newProperties = properties.filter((_, i) => i !== index);
    setProperties(newProperties);
    updateSchema(newProperties);
  };

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card className="shadow-sm">
        <CardHeader className="border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <FileText className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <CardTitle>Call Summary</CardTitle>
              <CardDescription>
                Configure how call transcripts are summarized
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              <Label>Summary Prompt</Label>
            </div>
            <Textarea
              value={assistant?.analysisPlan?.summaryPrompt ?? ''}
              onChange={(e) => handleSummaryUpdate(e.target.value)}
              className="min-h-[120px]"
              placeholder='You are an expert note-taker. You will be given a transcript of a call. Summarize the call in 2-3 sentences, if applicable.'
            />
            <p className="text-xs text-muted-foreground">
              Prompt used to generate call summaries from transcripts
            </p>
          </div>

          <SliderWithInput
            icon={Clock}
            label="Request Timeout"
            subline="Maximum seconds to wait for summary generation"
            min={1}
            max={60}
            step={1}
            leftLabel='1 sec'
            rightLabel='60 sec'
            value={assistant?.analysisPlan?.summaryRequestTimeoutSeconds || 20}
            defaultValue={20}
            onChange={(value) => handleUpdate(['analysisPlan', 'summaryRequestTimeoutSeconds'], value)}
            inputWidth={70}
          />
        </CardContent>
      </Card>

      {/* Success Evaluation Card */}
      <Card className="shadow-sm">
        <CardHeader className="border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <CheckCircle2 className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <CardTitle>Success Evaluation</CardTitle>
              <CardDescription>
                Evaluate call success using custom criteria
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              <Label>Evaluation Prompt</Label>
            </div>
            <Textarea
              value={assistant?.analysisPlan?.successEvaluationPrompt ?? ''}
              onChange={(e) => handleEvaluationUpdate(e.target.value)}
              className="min-h-[120px]"
              placeholder="You are an expert call evaluator. You will be given a transcript of a call..."
            />
            <p className="text-xs text-muted-foreground">
              Prompt used to evaluate if the call achieved its objectives
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-primary" />
              <Label>Evaluation Rubric</Label>
            </div>
            <Combobox
              options={[
                { value: 'NumericScale', label: 'Numeric Scale', description: 'Scale of 1 to 10' },
                { value: 'DescriptiveScale', label: 'Descriptive Scale', description: 'Excellent, Good, Fair, Poor' },
                { value: 'Checklist', label: 'Checklist', description: 'Criteria checklist with status' },
                { value: 'Matrix', label: 'Matrix', description: 'Multi-criteria grid evaluation' },
                { value: 'PercentageScale', label: 'Percentage Scale', description: 'Scale of 0% to 100%' },
                { value: 'LikertScale', label: 'Likert Scale', description: 'Strongly Agree to Strongly Disagree' },
                { value: 'AutomaticRubric', label: 'Automatic Rubric', description: 'Auto-generated criteria scoring' },
                { value: 'PassFail', label: 'Pass/Fail', description: 'Simple true/false evaluation' }
              ]}
              value={assistant?.analysisPlan?.successEvaluationRubric || 'NumericScale'}
              onValueChange={(value) => handleUpdate(['analysisPlan', 'successEvaluationRubric'], value)}
              placeholder="Select rubric..."
              searchPlaceholder="Search rubrics..."
              emptyMessage="No rubric found."
            />
            <p className="text-xs text-muted-foreground">
              Choose the evaluation method for measuring call success
            </p>
          </div>

          <SliderWithInput
            icon={Clock}
            label="Request Timeout"
            subline="Maximum seconds to wait for evaluation"
            min={1}
            max={60}
            step={1}
            leftLabel='1 sec'
            rightLabel='60 sec'
            value={assistant?.analysisPlan?.successEvaluationRequestTimeoutSeconds || 20}
            defaultValue={20}
            onChange={(value) => handleUpdate(['analysisPlan', 'successEvaluationRequestTimeoutSeconds'], value)}
            inputWidth={70}
          />
        </CardContent>
      </Card>

      {/* Structured Data Card */}
      <Card className="shadow-sm">
        <CardHeader className="border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Database className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <CardTitle>Structured Data Extraction</CardTitle>
              <CardDescription>
                Extract specific data fields from call transcripts
              </CardDescription>
            </div>
          </div>
        </CardHeader>
         
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              <Label>Extraction Prompt</Label>
            </div>
            <Textarea
              value={assistant?.analysisPlan?.structuredDataPrompt ?? ''}
              onChange={(e) => handleStructuredDataUpdate(e.target.value)}
              placeholder="You are an expert data extractor. Extract structured data from the transcript..."
              className="min-h-[120px]"
            />
            <p className="text-xs text-muted-foreground">
              Prompt used to extract structured data from conversations
            </p>
          </div>

          <SliderWithInput
            icon={Clock}
            label="Request Timeout"
            subline="Maximum seconds to wait for data extraction"
            min={1}
            max={60}
            step={1}
            leftLabel='1 sec'
            rightLabel='60 sec'
            value={assistant?.analysisPlan?.structuredDataRequestTimeoutSeconds || 10}
            defaultValue={10}
            onChange={(value) => handleUpdate(['analysisPlan', 'structuredDataRequestTimeoutSeconds'], value)}
            inputWidth={70}
          />
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <FileJson className="h-4 w-4 text-primary" />
                <Label>Schema Properties</Label>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={addProperty}
                className="gap-2 h-9"
              >
                <Plus className="h-4 w-4" />
                Add Property
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Define the structure of data to extract from calls
            </p>

            {properties.map((property, index) => (
              <Card key={index} className="shadow-sm">
                <CardContent className="p-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Property Name</Label>
                        <Input
                          value={property.name}
                          onChange={(e) => updateProperty(index, 'name', e.target.value)}
                          placeholder="e.g., customer_name"
                          className="h-9"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Data Type</Label>
                        <Combobox
                          options={dataTypes.map(type => ({ value: type, label: type }))}
                          value={property.type}
                          onValueChange={(value) => updateProperty(index, 'type', value)}
                          placeholder="Select type..."
                          searchPlaceholder="Search types..."
                          emptyMessage="No type found."
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`required-${index}`}
                          checked={property.required}
                          onCheckedChange={(checked) =>
                            updateProperty(index, 'required', checked as boolean)
                          }
                        />
                        <label
                          htmlFor={`required-${index}`}
                          className="text-sm font-medium cursor-pointer"
                        >
                          Required field
                        </label>
                      </div>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeProperty(index)}
                              className="text-destructive hover:text-destructive h-9 w-9"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Remove property</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    {property.description !== undefined ? (
                      <div className="space-y-2">
                        <Label>Description (Optional)</Label>
                        <Input
                          value={property.description}
                          onChange={(e) => updateProperty(index, 'description', e.target.value)}
                          placeholder="Describe this property..."
                          className="h-9"
                        />
                      </div>
                    ) : (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 h-auto text-xs"
                        onClick={() => updateProperty(index, 'description', '')}
                      >
                        + Add Description
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {properties.length === 0 && (
              <div className="text-center py-8 text-sm text-muted-foreground">
                No properties defined. Click "Add Property" to create a schema.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}